import { tryCatch } from '@cometchat/calls-sdk-core';
import * as v from 'valibot';
import type { Result } from './types';

const CometChatAPIErrorSchema = v.object({
  message: v.string(),
  devMessage: v.string(),
  source: v.string(),
  code: v.string(),
});

export type CometChatAPIError = v.InferInput<typeof CometChatAPIErrorSchema>;

export interface CometChatAPIException extends Error {
  readonly name:
    | 'COMET_CHAT_API_ERROR'
    | 'NETWORK_ERROR'
    | 'VALIDATION_ERROR'
    | 'BAD_RESPONSE'
    | 'UNKNOWN_ERROR';
  readonly details?: unknown;
  readonly timestamp: number;
}

export const HTTPRequestConfigSchema = v.object({
  baseURL: v.string(),
  method: v.optional(
    v.union([
      v.literal('GET'),
      v.literal('POST'),
      v.literal('PUT'),
      v.literal('DELETE'),
    ]),
    'GET',
  ),
  endpoint: v.string(),
  headers: v.record(v.string(), v.string()),
  body: v.optional(v.unknown()),
});

export type HTTPRequestConfig = v.InferInput<typeof HTTPRequestConfigSchema>;

export async function executeHTTPRequest<T = unknown>({
  baseURL,
  method = 'GET',
  endpoint,
  headers,
  body,
}: HTTPRequestConfig): Promise<Result<T, CometChatAPIException>> {
  // Validate input parameters
  const validationResult = v.safeParse(HTTPRequestConfigSchema, {
    baseURL,
    method,
    endpoint,
    headers,
    body,
  });

  if (!validationResult.success) {
    return {
      data: null,
      error: {
        name: 'VALIDATION_ERROR',
        message:
          'Invalid request parameters: ' +
          validationResult.issues.map(issue => issue.message).join(', '),
        timestamp: Date.now(),
      },
    };
  }

  // Construct the CometChat API URL
  const apiUrl = `${baseURL}/${endpoint}`;

  // Prepare fetch options
  const fetchOptions: RequestInit = {
    method,
    headers,
  };

  // Include body for non-GET requests
  if (body && method !== 'GET') {
    fetchOptions.body = typeof body === 'string' ? body : JSON.stringify(body);
  }

  // Execute the HTTP request with error handling
  const { data: response, error: fetchError } = await tryCatch(
    fetch(apiUrl, fetchOptions),
  );

  if (fetchError) {
    return {
      data: null,
      error: {
        name: 'NETWORK_ERROR',
        message: `Network request failed: ${fetchError.message}`,
        details: fetchError,
        timestamp: Date.now(),
      },
    };
  }

  // Parse JSON response with error handling
  const { data: responseJson, error: jsonError } = await tryCatch(
    response.json(),
  );

  if (jsonError) {
    return {
      data: null,
      error: {
        name: 'UNKNOWN_ERROR',
        message: `Failed to parse response JSON: ${jsonError.message}`,
        timestamp: Date.now(),
      },
    };
  }

  // Handle API-level errors from CometChat server
  if (responseJson.error) {
    const apiErrorValidation = v.safeParse(
      CometChatAPIErrorSchema,
      responseJson.error,
    );

    if (!apiErrorValidation.success) {
      return {
        data: null,
        error: {
          name: 'UNKNOWN_ERROR',
          message: 'Unknown API error format received from server',
          timestamp: Date.now(),
        },
      };
    }

    return {
      data: null,
      error: {
        name: 'COMET_CHAT_API_ERROR',
        message: apiErrorValidation.output.message,
        details: apiErrorValidation.output,
        timestamp: Date.now(),
      },
    };
  }

  // Return successful response data
  return {
    data: responseJson.data,
    error: null,
  };
}
